<script setup lang="ts">
/**
 * Donut chart with the total in its centre and a legend that doubles as the
 * selection: a legend row (or an arc) selects its segment, a second click
 * clears it. The caller reacts to the selection — typically by opening the
 * list behind the segment under the chart.
 *
 * Presentational only: segments come in as props, the selection goes out as
 * `v-model:selected` and `select`. The legend is the accessible form of the
 * chart (label and value per row, pressed state), so the SVG itself is hidden
 * from assistive technology.
 */
import { ChevronRightIcon } from '@blpde/nx-icons';
import { computed, inject, ref, useTemplateRef } from 'vue';
import { i18nInjectionKey } from '../internal';
import {
  donutSegmentColor,
  donutSum,
  layoutDonut,
  type NxDonutArc,
  type NxDonutKey,
  type NxDonutSegment,
} from './donut';

const props = withDefaults(
  defineProps<{
    /** Segments in drawing order (clockwise from 12 o'clock) and legend order. */
    segments: readonly NxDonutSegment[];
    /** Figure in the centre. Defaults to the sum of the values. */
    total?: number;
    /** Caption under the centre figure („offen“). */
    centerLabel?: string;
    /** Accessible name of the chart („Aufgaben nach Status“). */
    ariaLabel?: string;
    /**
     * Diameter of the ring. Defaults to `md`. `fill` grows the ring with the
     * height of the box it sits in (a dashboard tile), the legend beside it.
     */
    size?: 'sm' | 'md' | 'lg' | 'fill';
    /** What the legend rows show: the value, its share of the total, or both. */
    legendValue?: 'value' | 'share' | 'both';
    /**
     * Legend rows carry a chevron that turns while the row is selected — for a
     * legend that opens a list under the chart.
     */
    expandable?: boolean;
    /** Formats values and the total. Defaults to a plain number in the app's locale. */
    formatValue?: (value: number) => string;
  }>(),
  {
    total: undefined,
    centerLabel: undefined,
    ariaLabel: undefined,
    size: 'md',
    legendValue: 'value',
    expandable: false,
    formatValue: undefined,
  }
);

/** Key of the selected segment, `null` for none. */
const selected = defineModel<NxDonutKey | null>('selected', { default: null });

const emit = defineEmits<{
  /** The user changed the selection; `null` when a second click cleared it. */
  select: [key: NxDonutKey | null, segment: NxDonutSegment | null];
}>();

// Ring geometry in viewBox units (0 0 100 100).
const RADIUS = 40;
const STROKE = 14;
const STROKE_SELECTED = 18;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
/** Surface gap between two arcs — about 2px at the default size. */
const GAP = 2;

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => i18n?.locale.value);

const numberFormat = computed(() => new Intl.NumberFormat(locale.value));
const percentFormat = computed(
  () => new Intl.NumberFormat(locale.value, { style: 'percent', maximumFractionDigits: 1 })
);

function format(value: number): string {
  return props.formatValue ? props.formatValue(value) : numberFormat.value.format(value);
}

const arcs = computed(() => layoutDonut(props.segments, CIRCUMFERENCE, GAP));
const centerValue = computed(() => props.total ?? donutSum(props.segments));

/** Segment under the pointer (arc or legend row) or in keyboard focus. */
const hovered = ref<NxDonutKey | null>(null);
/** The segment the chart shows in full while the others fade. */
const emphasis = computed(() => hovered.value ?? selected.value);

/** The centre speaks for the emphasised segment, else for the whole. */
const centerFocus = computed(() => {
  const key = emphasis.value;
  if (key === null) return null;
  const index = props.segments.findIndex((s) => s.key === key);
  if (index < 0) return null;
  const segment = props.segments[index]!;
  const arc = arcs.value.find((a) => a.key === key);
  return {
    value: format(segment.value),
    caption: segment.label,
    share: arc ? percentFormat.value.format(arc.share) : '',
  };
});

function legendText(segment: NxDonutSegment): string {
  const sum = donutSum(props.segments);
  const value = Number.isFinite(segment.value) && segment.value > 0 ? segment.value : 0;
  const share = sum > 0 ? percentFormat.value.format(value / sum) : '–';
  if (props.legendValue === 'share') return share;
  if (props.legendValue === 'both') return `${format(segment.value)} · ${share}`;
  return format(segment.value);
}

function arcTitle(arc: NxDonutArc): string {
  const segment = props.segments[arc.index];
  return `${segment.label}: ${format(segment.value)} (${percentFormat.value.format(arc.share)})`;
}

function toggle(segment: NxDonutSegment) {
  const next = selected.value === segment.key ? null : segment.key;
  selected.value = next;
  emit('select', next, next === null ? null : segment);
}

const legendRef = useTemplateRef<HTMLElement>('legend');

/** Arrow keys, Home and End walk the legend rows; each row stays a tab stop. */
function onLegendKeydown(event: KeyboardEvent) {
  const rows = [...(legendRef.value?.querySelectorAll<HTMLButtonElement>('button') ?? [])];
  const index = rows.indexOf(event.target as HTMLButtonElement);
  if (index < 0) return;
  let next: number;
  switch (event.key) {
    case 'ArrowDown':
    case 'ArrowRight':
      next = Math.min(index + 1, rows.length - 1);
      break;
    case 'ArrowUp':
    case 'ArrowLeft':
      next = Math.max(index - 1, 0);
      break;
    case 'Home':
      next = 0;
      break;
    case 'End':
      next = rows.length - 1;
      break;
    default:
      return;
  }
  event.preventDefault();
  rows[next].focus();
}
</script>

<template>
  <div
    class="nx-donut-chart"
    :class="[`size-${props.size}`, { 'is-expandable': props.expandable }]"
    role="group"
    :aria-label="props.ariaLabel"
  >
    <div class="nx-donut-chart__body">
      <div class="nx-donut-chart__plot">
        <svg class="nx-donut-chart__svg" viewBox="0 0 100 100" aria-hidden="true">
          <circle
            v-if="!arcs.length"
            class="nx-donut-chart__track"
            cx="50"
            cy="50"
            :r="RADIUS"
            fill="none"
            :stroke-width="STROKE"
          />
          <circle
            v-for="arc in arcs"
            :key="arc.key"
            class="nx-donut-chart__arc"
            :class="{ 'is-dimmed': emphasis !== null && emphasis !== arc.key }"
            :data-key="arc.key"
            cx="50"
            cy="50"
            :r="RADIUS"
            fill="none"
            :stroke-width="selected === arc.key ? STROKE_SELECTED : STROKE"
            :stroke-dasharray="`${arc.length} ${CIRCUMFERENCE - arc.length}`"
            :stroke-dashoffset="-arc.start"
            transform="rotate(-90 50 50)"
            :style="{ stroke: arc.color }"
            @click="toggle(props.segments[arc.index])"
            @mouseenter="hovered = arc.key"
            @mouseleave="hovered = null"
          >
            <title>{{ arcTitle(arc) }}</title>
          </circle>
        </svg>
        <div v-if="centerFocus" class="nx-donut-chart__center is-focus" aria-hidden="true">
          <span class="nx-donut-chart__total">{{ centerFocus.value }}</span>
          <span class="nx-donut-chart__caption">{{ centerFocus.caption }}</span>
          <span v-if="centerFocus.share" class="nx-donut-chart__share">
            {{ centerFocus.share }}
          </span>
        </div>
        <div v-else class="nx-donut-chart__center">
          <span class="nx-donut-chart__total">{{ format(centerValue) }}</span>
          <!-- The leading space keeps „12 offen“ two words when read as text;
             on screen the caption is a line of its own. -->
          <span v-if="props.centerLabel" class="nx-donut-chart__caption">
            {{ ` ${props.centerLabel}` }}
          </span>
        </div>
      </div>
      <ul
        v-if="props.segments.length"
        ref="legend"
        class="nx-donut-chart__legend"
        role="list"
        @keydown="onLegendKeydown"
      >
        <li v-for="(segment, index) in props.segments" :key="segment.key">
          <button
            type="button"
            class="nx-donut-chart__row"
            :class="{ 'is-highlighted': hovered === segment.key }"
            :aria-pressed="selected === segment.key"
            @click="toggle(segment)"
            @mouseenter="hovered = segment.key"
            @mouseleave="hovered = null"
            @focus="hovered = segment.key"
            @blur="hovered = null"
          >
            <span
              class="nx-donut-chart__swatch"
              :style="{ background: donutSegmentColor(segment.tone, index) }"
              aria-hidden="true"
            />
            <span class="nx-donut-chart__label" :title="segment.label">{{ segment.label }}</span>
            <!-- Leading space: the row's name reads „Offen 4“, not „Offen4“. -->
            <span class="nx-donut-chart__value">{{ ` ${legendText(segment)}` }}</span>
            <ChevronRightIcon
              v-if="props.expandable"
              class="nx-donut-chart__chevron nxt-icon-sm"
              aria-hidden="true"
            />
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
/* The root is the size container; the body lays ring and legend out by it. */
.nx-donut-chart {
  --donut-size: var(--nxt-area-size-md);
  --donut-total-size: var(--nxt-font-size-2xl);

  container: nx-donut / inline-size;
}
.nx-donut-chart__body {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-md) var(--nxt-space-lg);
}
.nx-donut-chart.size-sm {
  --donut-size: var(--nxt-area-size-sm);
  --donut-total-size: var(--nxt-font-size-lg);
}
.nx-donut-chart.size-lg {
  --donut-size: var(--nxt-area-size-lg);
  --donut-total-size: calc(var(--nxt-size-unit) * 2);
}
/* Ring as tall as the box, legend beside it; the figure scales with the ring. */
.nx-donut-chart.size-fill {
  height: 100%;
  min-height: var(--nxt-area-size-sm);
}
.nx-donut-chart.size-fill .nx-donut-chart__body {
  flex-wrap: nowrap;
  height: 100%;
}
.nx-donut-chart.size-fill .nx-donut-chart__plot {
  container-type: size;
  width: auto;
  height: 100%;
  max-width: 45%;
}
.nx-donut-chart.size-fill .nx-donut-chart__legend {
  flex: 1 1 auto;
}
/* Too narrow for ring and legend side by side: legend under the ring. */
@container nx-donut (max-width: 380px) {
  .nx-donut-chart.size-fill .nx-donut-chart__body {
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: stretch;
  }
  .nx-donut-chart.size-fill .nx-donut-chart__plot {
    align-self: center;
    flex: 1 1 auto;
    width: auto;
    min-height: var(--nxt-area-size-sm);
    max-width: 100%;
  }
  .nx-donut-chart.size-fill .nx-donut-chart__legend {
    flex: 0 0 auto;
    max-width: none;
  }
}
.nx-donut-chart.size-fill .nx-donut-chart__total {
  font-size: clamp(var(--nxt-font-size-md), 17cqi, calc(var(--nxt-size-unit) * 2.25));
}

.nx-donut-chart__plot {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: var(--donut-size);
  aspect-ratio: 1;
}
.nx-donut-chart__svg,
.nx-donut-chart__center {
  grid-area: 1 / 1;
}
.nx-donut-chart__svg {
  display: block;
  width: 100%;
  height: 100%;
}
.nx-donut-chart__track {
  stroke: var(--nxt-border);
}
.nx-donut-chart__arc {
  cursor: pointer;
  transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-donut-chart__arc.is-dimmed {
  opacity: 0.35;
}

/* Over the ring's hole; clicks go through to the arcs. */
.nx-donut-chart__center {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 60%;
  line-height: var(--nxt-line-height-tight);
  text-align: center;
  pointer-events: none;
}
.nx-donut-chart__total {
  font-size: var(--donut-total-size);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: -0.02em;
  color: var(--nxt-fg);
}
.nx-donut-chart__caption {
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
  overflow-wrap: anywhere;
}
.nx-donut-chart__center.is-focus .nx-donut-chart__caption {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.nx-donut-chart__share {
  margin-block-start: var(--nxt-space-2xs);
  padding: 0 var(--nxt-space-xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-neutral-subtle-bg);
  color: var(--nxt-neutral-subtle-fg);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
}

.nx-donut-chart__legend {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  flex: 1 1 var(--nxt-panel-width-xs);
  min-width: 0;
  max-width: var(--nxt-panel-width-md);
  margin: 0;
  padding: 0;
  list-style: none;
}
.nx-donut-chart__row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--nxt-space-xs);
  width: 100%;
  min-height: var(--nxt-control-size-sm);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  text-align: start;
  cursor: pointer;
}
.nx-donut-chart.is-expandable .nx-donut-chart__row {
  grid-template-columns: auto minmax(0, 1fr) auto auto;
}
.nx-donut-chart__row:hover,
.nx-donut-chart__row.is-highlighted {
  background: var(--nxt-table-row-hover-bg);
}
.nx-donut-chart__row[aria-pressed='true'] {
  background: var(--nxt-table-row-active-bg);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-donut-chart__row:focus-visible {
  outline: none;
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-donut-chart__swatch {
  width: var(--nxt-dot-size-md);
  height: var(--nxt-dot-size-md);
  border-radius: var(--nxt-radius-full);
}
.nx-donut-chart__label {
  /* Never squeezed to nothing by a long value: a name keeps a few glyphs. */
  min-width: calc(var(--nxt-size-unit) * 3);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-donut-chart__value {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  font-weight: var(--nxt-font-weight-semibold);
  text-align: end;
}
.nx-donut-chart__chevron {
  color: var(--nxt-fg-muted);
  transition: transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-donut-chart__row[aria-pressed='true'] .nx-donut-chart__chevron {
  transform: rotate(90deg);
}

@media (prefers-reduced-motion: reduce) {
  .nx-donut-chart__arc,
  .nx-donut-chart__chevron {
    transition: none;
  }
}

/* Forced colours drop the row backgrounds: mark the selected row with an outline. */
@media (forced-colors: active) {
  .nx-donut-chart__row[aria-pressed='true'] {
    outline: calc(var(--nxt-hairline) * 2) solid Highlight;
  }
}
</style>
